import { useCallback, useState } from "react";
import { UseDisclosureReturn, useDisclosure } from "hooks/use-disclosure";

import toast from "components/partials/toast/toast";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useCurrentUser } from "hooks/api/use-current-user";
import {
  useClaimVersionLock,
  useSetTouchpointVersionCache,
  type TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { mapTouchpointVersionDTO } from "utilities/api-mappers";

import { isLocked } from "utilities";
import { useTouchpointLocking } from "contexts/touchpoint-locking-context";
import { BuilderContent } from "types";

const touchpointVersionApi = getTouchpointVersion();

interface UseVersionsActionMenuItemsProps {
  newDraftModal: UseDisclosureReturn;
}

const useVersionsActionMenuItems = ({ newDraftModal }: UseVersionsActionMenuItemsProps) => {
  const currentClient = useCurrentClient();
  const currentUser = useCurrentUser();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();

  const editVersionModal = useDisclosure();

  const {
    setIsLockingModalOpen,
    setOnTakeOverEditing,
    setSelectedVersion: setLockingContextVersion,
  } = useTouchpointLocking();

  const [currentActionItem, setCurrentActionItem] = useState<TouchpointVersionAttributes>();

  const [selectedVersion, setSelectedVersion] = useState<TouchpointVersionAttributes>();

  const versionScope = {
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  };
  const setTouchpointVersionCache = useSetTouchpointVersionCache(versionScope);
  const claimLockMutation = useClaimVersionLock(versionScope);
  const [lockedVersionIds, setLockedVersionIds] = useState<Record<string, boolean>>({});

  const handleCreateDraftFromVersion = useCallback(() => {
    newDraftModal.onOpen();
  }, [newDraftModal]);

  const handleAfterTakeOverEditing = useCallback(
    (version: TouchpointVersionAttributes) => {
      setCurrentActionItem(version);
      editVersionModal.onOpen();
      setOnTakeOverEditing(null);
    },
    [editVersionModal, setOnTakeOverEditing],
  );

  const handleEditVersionDetails = useCallback(
    (version: TouchpointVersionAttributes) => {
      touchpointVersionApi
        .getOneVersion({
          clientId: currentClient.id,
          campaignId: currentCampaign.id,
          touchpointId: currentTouchpoint.id,
          id: version.id,
        })
        .then((dto) => {
          const fresh = mapTouchpointVersionDTO(dto);
          if (isLocked(fresh as unknown as BuilderContent, currentUser)) {
            // First, set the version in the locking context
            setLockingContextVersion(fresh);
            // Then set the callback
            setOnTakeOverEditing(() => () => {
              handleAfterTakeOverEditing(fresh);
            });
            // Finally, open the modal
            setIsLockingModalOpen(true);
          } else {
            setCurrentActionItem(fresh);
            claimLockMutation.mutate(fresh.id);
            editVersionModal.onOpen();
          }
        })
        .catch((error) => {
          console.error("Failed to load touchpoint version for edit", error);
          toast.error({
            data: {
              title: "Failed to load version details. Please try again.",
            },
          });
        });
    },
    [
      currentClient.id,
      currentCampaign.id,
      currentTouchpoint.id,
      currentUser,
      editVersionModal,
      setLockingContextVersion,
      setOnTakeOverEditing,
      setIsLockingModalOpen,
      handleAfterTakeOverEditing,
      claimLockMutation,
    ],
  );

  const verifyLockAndProceed = useCallback(
    async (version: TouchpointVersionAttributes, callback: () => void) => {
      try {
        const dto = await touchpointVersionApi.getOneVersion({
          clientId: currentClient.id,
          campaignId: currentCampaign.id,
          touchpointId: currentTouchpoint.id,
          id: version.id,
        });
        const freshVersion = mapTouchpointVersionDTO(dto);

        // Check if it's locked by someone else using the isLocked utility
        const isLockedByAnother = isLocked(
          freshVersion as unknown as BuilderContent,
          currentUser,
        );

        // Update the locked versions state
        setLockedVersionIds((prev) => ({
          ...prev,
          [version.id]: isLockedByAnother,
        }));

        setTouchpointVersionCache(freshVersion);

        if (isLockedByAnother) {
          toast.warning({
            data: {
              title: "Action not permitted",
              message: `This version is currently locked by ${freshVersion.lockedBy?.firstName} ${freshVersion.lockedBy?.lastName}`,
            },
          });
        } else {
          // Not locked, proceed with the action
          callback();
        }
      } catch (error) {
        console.error("Error verifying lock status:", error);
        toast.error({
          data: {
            title: "Failed to verify lock status",
          },
        });
      }
    },
    [
      currentClient.id,
      currentCampaign.id,
      currentTouchpoint.id,
      currentUser,
      setTouchpointVersionCache,
      setLockedVersionIds,
    ],
  );

  // Check if a version is locked, using our updated state if available
  const checkIfLocked = (version: TouchpointVersionAttributes) => {
    if (version.id in lockedVersionIds) {
      return lockedVersionIds[version.id];
    }
    return isLocked(version as unknown as BuilderContent, currentUser);
  };

  return {
    // Actions menu handlers
    selectedVersion,
    setCurrentActionItem,
    currentActionItem,
    setSelectedVersion,
    verifyLockAndProceed,
    checkIfLocked,
    handleCreateDraftFromVersion,
    handleEditVersionDetails,
    handleAfterTakeOverEditing,
  };
};

export default useVersionsActionMenuItems;
